import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Paper,
  Group,
  Button,
  Collapse,
  Alert,
  Badge,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconEye,
} from "@tabler/icons-react";
import { useResponsive } from "../../context/ResponsiveContext";
import { useSalon } from "../../context/SalonContext";
import { getSalonTimezone } from "../../utils/timeUtils";
import { appointmentStatusDisplay } from "../calendar/CalendarView/booking/guards";
import { GetTransactionsResponse } from "../../types/transactions";
import TransactionDetailModal from "../calendar/CalendarView/components/TransactionDetailModal/TransactionDetailModal";
import { isTransactionInFinalState } from "../../utils/transactionUtils";
import styles from "./IncompleteTransactionsModal.module.css";

interface IncompleteTransactionsModalProps {
  opened: boolean;
  onClose: () => void;
  transactions: GetTransactionsResponse[];
  onProceedAnyway: () => void;
  onAllComplete?: () => void;
}

export default function IncompleteTransactionsModal({
  opened,
  onClose,
  transactions,
  onProceedAnyway,
  onAllComplete,
}: IncompleteTransactionsModalProps) {
  const { isMobile } = useResponsive();
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone?: string } | null;
  };
  const timezone = getSalonTimezone(businessInfo);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [transactionModalOpened, setTransactionModalOpened] = useState(false);
  const [currentTransactions, setCurrentTransactions] =
    useState<GetTransactionsResponse[]>(transactions);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (opened) {
      setCurrentTransactions(transactions);
      setHasInitialized(transactions.length > 0);
    } else {
      // Reset all modal state when closing
      setHasInitialized(false);
      setTransactionModalOpened(false);
      setSelectedTransaction(null);
      setIsExpanded(false);
    }
  }, [opened, transactions]);

  // Check if all transactions are complete whenever the list changes
  useEffect(() => {
    // Only call onAllComplete if:
    // 1. Modal is opened
    // 2. We had transactions initially (hasInitialized is true)
    // 3. Now we have zero transactions (they were all resolved)
    if (
      opened &&
      hasInitialized &&
      currentTransactions.length === 0 &&
      onAllComplete
    ) {
      onAllComplete();
    }
  }, [currentTransactions, opened, hasInitialized, onAllComplete]);

  const handleViewTransaction = (transaction: GetTransactionsResponse) => {
    setSelectedTransaction(transaction);
    setTransactionModalOpened(true);
  };

  const handleTransactionUpdated = (
    updatedTransaction: GetTransactionsResponse | any,
  ) => {
    // Update the transaction in the list if it's a GetTransactionsResponse
    if ("reminder_messages" in updatedTransaction) {
      const txn = updatedTransaction as GetTransactionsResponse;

      // Update the selected transaction
      setSelectedTransaction(txn);

      // Update the current transactions list
      setCurrentTransactions((prev) => {
        const updated = prev.map((t) =>
          t.transaction.id === txn.transaction.id ? txn : t,
        );

        // Filter out transactions that are now in final states
        return updated.filter((t) => !isTransactionInFinalState(t));
      });
    }
  };

  const handleTransactionModalClose = () => {
    setTransactionModalOpened(false);
    setSelectedTransaction(null);
  };

  // Helper function to get service names from transaction
  const getServiceNames = (transaction: GetTransactionsResponse): string => {
    const services = transaction.line_items.booked_services || [];
    if (services.length === 0) return "Quick Sale";
    return services.map((s) => s.name).join(", ");
  };

  // Helper function to get client name
  const getClientName = (transaction: GetTransactionsResponse): string => {
    const client = transaction.client;
    return `${client.first_name} ${client.last_name}`;
  };

  // Helper function to get staff name from transaction
  const getStaffName = (transaction: GetTransactionsResponse): string => {
    const services = transaction.line_items.booked_services || [];
    if (services.length === 0) {
      // For quick sales, try to get staff from products
      const products = transaction.line_items.products || [];
      if (products.length > 0) {
        // Products don't have staff_name, so we'll return "N/A" for quick sales
        return "N/A";
      }
      return "N/A";
    }
    // Get unique staff names
    const staffNamesSet = new Set(
      services.map((s) => s.staff_name).filter(Boolean),
    );
    const staffNames = Array.from(staffNamesSet);
    return staffNames.join(", ") || "N/A";
  };

  // Helper function to get appointment status color (matches TransactionDetails)
  const getAppointmentStatusColor = (status: string): string => {
    switch (status) {
      case "Completed":
        return "green";
      case "Requested":
        return "yellow";
      case "Accepted":
        return "orange";
      case "Confirmed":
        return "blue";
      case "Arrived":
        return "limeGreen";
      case "No Show":
        return "red";
      case "Cancelled":
      case "Declined":
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title="Incomplete Appointments Detected"
        size="lg"
        fullScreen={isMobile}
        centered
      >
        <Stack gap="md">
          <Alert
            color="var(--color-accent)"
            icon={<IconAlertCircle size={20} />}
          >
            <Text size="sm">
              Please checkout any incomplete appointments to ensure the correct
              amounts are being synced.
            </Text>
          </Alert>

          <Paper>
            <Group
              justify="space-between"
              style={{ cursor: "pointer" }}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Group gap="xs">
                {isExpanded ? (
                  <IconChevronDown size={20} />
                ) : (
                  <IconChevronRight size={20} />
                )}
                <Text fw={500}>
                  View {currentTransactions.length} Incomplete Appointment
                  {currentTransactions.length !== 1 ? "s" : ""}
                </Text>
              </Group>
            </Group>

            <Collapse in={isExpanded}>
              <Stack gap="xs" mt="md">
                {currentTransactions.length > 0 ? (
                  currentTransactions.map((transaction) => {
                    const serviceName = getServiceNames(transaction);
                    const clientName = getClientName(transaction);
                    const staffName = getStaffName(transaction);
                    const appointmentStatus =
                      transaction.transaction.appointment_status || "Pending";
                    // Display-only "Accepted (Unconfirmed)" (Accepted within
                    // 48h of start).
                    const statusDisplay = appointmentStatusDisplay(
                      appointmentStatus,
                      transaction,
                      timezone,
                    );

                    return (
                      <Paper
                        key={transaction.transaction.id}
                        p="sm"
                        withBorder
                        className={styles.transactionItem}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>
                              {serviceName} for {clientName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              Serviced by {staffName}
                            </Text>
                            <Group gap="xs" mt={4}>
                              <Badge
                                size="sm"
                                color={
                                  statusDisplay.awaitingConfirmation
                                    ? statusDisplay.color
                                    : getAppointmentStatusColor(
                                        appointmentStatus,
                                      )
                                }
                              >
                                {statusDisplay.label}
                              </Badge>
                            </Group>
                          </div>
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconEye size={14} />}
                            onClick={() => handleViewTransaction(transaction)}
                          >
                            View
                          </Button>
                        </Group>
                      </Paper>
                    );
                  })
                ) : (
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    All appointments have been resolved!
                  </Text>
                )}
              </Stack>
            </Collapse>
          </Paper>

          <Group justify="flex-end" gap="sm">
            {currentTransactions.length > 0 && (
              <Button color="var(--color-primary)" onClick={onProceedAnyway}>
                Proceed
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <TransactionDetailModal
          opened={transactionModalOpened}
          onClose={handleTransactionModalClose}
          transaction={selectedTransaction}
          onTransactionUpdated={handleTransactionUpdated}
        />
      )}
    </>
  );
}
